/**
 * CO-804 — the single owner of "what string goes next to an amount" on web.
 *
 * ## Why this exists
 *
 * 16 of the 153 currencies the server serves have a `currencySymbol` whose first strong character
 * has Unicode bidi class `AL` (Arabic Letter): `AFN BHD DZD IQD IRR JOD KWD LBP LYD MAD OMR QAR
 * SAR SDG TND YER`. Interpolating one of those in front of a number — `` `${symbol} ${n}` `` —
 * opens an RTL run that **swallows the trailing digits**, so the logical string `؋ 50` renders
 * visually as `50 ؋`. Measured on a device, then computed exhaustively over the live payload.
 *
 * That is correct Unicode, not corrupt data (`CO-786` repaired the bytes; the bytes are right).
 * `INR` (U+20B9) and `THB` (U+0E3F) do **not** reorder — they are class `ET`, like `$ £ €`.
 * Only an `AL`/`R` first strong character reorders, and that is deterministic from Unicode
 * properties rather than something to sample.
 *
 * ## The decision (owner, 2026-09-21)
 *
 * **Render the ISO currency code, exactly as mobile and the payslip services already do.** An ISO
 * code is ASCII, bidi class `L`, so `SAR 50` and `50 SAR` both render in the intended order in an
 * LTR *and* an RTL paragraph. Wrapping the symbol in bidi isolates (FSI/PDI) was considered and
 * **rejected** — do not reintroduce a symbol anywhere on the display path.
 *
 * This mirrors flutter's `Currency.getDisplayCurrency()` (`eb6d18b`) and the server's
 * `CurrencyCode ?? CurrencySymbol` ordering, so all three clients agree.
 *
 * ## The `??` / `||` trap
 *
 * 🔴 **`??` falls back on null only, and `||` on falsy only — neither falls back on CORRUPT.**
 * A present-but-wrong value (`"?"`, a stray space, a tofu glyph) is neither null nor falsy, so a
 * `symbol ?? code` chain can never reach the code and fails *closed* on exactly the bad data it
 * was meant to degrade around. That is the bug the mobile fix hit. Hence the explicit emptiness
 * checks below: a candidate counts only if it is a string with non-whitespace content.
 *
 * ## The naming trap
 *
 * 🔴 A variable named `currencySymbol` that holds a **code** (and vice versa) is how three of
 * these sites hid from a grep today. Read the value, never the identifier. Anything on the
 * display path in this app is a *label* — `currencyLabel` — and the only place the raw
 * `currencySymbol` key is read is this file.
 */

/** Any object that may carry the two currency fields the API serves. */
export interface CurrencySource {
  currencyCode?: string | null;
  currencySymbol?: string | null;
}

/**
 * A candidate is usable only when it is a string with non-whitespace content.
 * Explicit on purpose — see "The `??` / `||` trap" above.
 */
function usable(value: unknown): string {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  return trimmed === '' ? '' : trimmed;
}

/**
 * The label to render next to an amount, resolved **code first** across every source given.
 *
 * Every source's `currencyCode` is tried before any source's `currencySymbol`, so a nested shape
 * such as `currencyDisplay(rate?.currency, rate)` prefers a code found at either level over a
 * symbol found at either level. Returns `''` when nothing is usable — callers keep their own
 * last-resort default via {@link pickCurrencyLabel}.
 */
export function currencyDisplay(
  ...sources: ReadonlyArray<CurrencySource | null | undefined>
): string {
  for (const source of sources) {
    const code = usable(source?.currencyCode);
    if (code !== '') return code;
  }
  for (const source of sources) {
    const symbol = usable(source?.currencySymbol);
    if (symbol !== '') return symbol;
  }
  return '';
}

/**
 * The first usable label from an ordered list of already-resolved strings.
 *
 * For call sites that pick between a row-level code and a page-level label, e.g.
 * `pickCurrencyLabel(payslip.currencyCode, currencyLabel)`. Use this instead of a `||` chain so
 * the emptiness check stays explicit and a whitespace-only value cannot win.
 */
export function pickCurrencyLabel(
  ...labels: ReadonlyArray<string | null | undefined>
): string {
  for (const label of labels) {
    const value = usable(label);
    if (value !== '') return value;
  }
  return '';
}
